/**
 * @module components/Link
 * @description Client-side routing link component using Reach Router.
 * Prevents full page reloads by intercepting clicks and using history API.
 */

import React, { useCallback } from 'react';
import { navigate as _navigate } from '@reach/router';
import { LinkProps } from './types';

/**
 * @component Link
 * @type {React.FC<LinkProps>}
 * @summary Client-side navigation link with optional browser history back support.
 * Intercepts click events and uses Reach Router's navigate for SPA behavior.
 * @param {LinkProps} props - Component props from types.ts
 * @returns {React.ReactElement} Anchor element with intercepted navigation
 */
export const Link = ({
  to,
  back,
  className,
  children,
}: LinkProps) => {
  const onClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      if (back && Boolean(window.history.state)) {
        _navigate(-1);
      } else {
        _navigate(to);
      }
    },
    [to, back],
  );

  return (
    <a onClick={onClick} href={to} className={className}>
      {children}
    </a>
  );
};

export const navigate = _navigate;
